import * as React from "react";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Portal from "@mui/material/Portal";
import { useNavigate } from "react-router-dom";
import BackdropLoadingScreen from "../../../common/BackdropLoadingScreen";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import { AUTOPATROL_UNAVAILABLE_BULK_PRODUCT_LABELS } from "./BulkUpdateWizardUtils";

type AutoPatrolScheduleRow = {
  id: number;
  title?: string;
  customer?: {
    id?: number;
  };
  devices?: Array<{
    id?: number;
    deviceId?: number;
  }>;
};

type ScheduleProduct = {
  id: number;
  schedule: number;
  metric_label: {
    id: number;
    name: string;
    display_name: string;
    order: number | null;
    product: {
      id: number;
      name: string;
      value: string;
      order: number | null;
    };
  };
  enabled: boolean;
};

type ProductBulkUpdateDialogProps = {
  open: boolean;
  schedules: AutoPatrolScheduleRow[];
  autoPatrolCameraProducts: Record<number, ScheduleProduct[]>;
  onClose: () => void;
};

const EDIT_MODE_PRODUCT_GROUPS = [
  {
    color: "#FF8400",
    children: [
      "Intruder",
      "Vehicle (grouped car, truck, bus)",
      "Bike (grouped bicycle, motorcycle)",
    ],
  },
  {
    color: "#2196F3",
    children: ["Crowd"],
  },
  {
    color: "#F44336",
    children: ["Fire", "Smoke"],
  },
  {
    color: "#03A9F4",
    children: ["Non-UPS"],
  },
  {
    color: "#4CAF50",
    children: [
      "Amazon",
      "DHL",
      "Fedex",
      "School Bus",
      "UPS",
      "USPS",
      "Fire Truck",
    ],
  },
];

const ORDERED_EDIT_MODE_PRODUCTS = EDIT_MODE_PRODUCT_GROUPS.flatMap(
  (group) => group.children
);

const AUTOPATROL_UNAVAILABLE_PRODUCT_LABEL_SET = new Set(
  AUTOPATROL_UNAVAILABLE_BULK_PRODUCT_LABELS
);

const isAutopatrolUnavailableProduct = (productName: string) =>
  AUTOPATROL_UNAVAILABLE_PRODUCT_LABEL_SET.has(productName);

const EDIT_MODE_COLOR_BY_PRODUCT = EDIT_MODE_PRODUCT_GROUPS.reduce(
  (accumulator, group) => {
    group.children.forEach((productName) => {
      accumulator[productName] = group.color;
    });
    return accumulator;
  },
  {} as Record<string, string>
);

const toCheckboxKey = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, "_");

const getProductDisplayLabel = (productName: string) => {
  if (productName === "Vehicle (grouped car, truck, bus)") {
    return "Vehicle";
  }
  if (productName === "Bike (grouped bicycle, motorcycle)") {
    return "Bike";
  }
  return productName;
};

const createInitialDraft = (
  schedules: AutoPatrolScheduleRow[],
  autoPatrolCameraProducts: Record<number, ScheduleProduct[]>
) => {
  const draft: Record<number, Record<string, boolean>> = {};

  schedules.forEach((schedule) => {
    const products = autoPatrolCameraProducts[schedule.id] || [];
    draft[schedule.id] = products.reduce((accumulator, product) => {
      const displayName = product?.metric_label?.display_name;
      if (!displayName || isAutopatrolUnavailableProduct(displayName)) return accumulator;
      accumulator[displayName] = Boolean(product.enabled);
      return accumulator;
    }, {} as Record<string, boolean>);
  });

  return draft;
};

export const ProductBulkUpdateDialog = ({
  open,
  schedules,
  autoPatrolCameraProducts,
  onClose,
}: ProductBulkUpdateDialogProps) => {
  const navigate = useNavigate();
  const {
    updateScheduleProducts,
    setAutoPatrolCameraProducts,
    prefetchBulkDialogActiveCameraProducts,
  } = useSiteAboutStore();
  const customerId = React.useMemo(() => {
    const scheduleCustomerId = schedules[0]?.customer?.id;
    if (scheduleCustomerId != null) {
      return Number(scheduleCustomerId);
    }
    const siteInfoFromStorage = JSON.parse(localStorage.getItem("siteInfo") || "{}");
    return Number(siteInfoFromStorage?.id || 0);
  }, [schedules]);
  const affectedCameraIds = React.useMemo(() => {
    const ids = new Set<number>();
    schedules.forEach((schedule) => {
      (schedule.devices || []).forEach((device) => {
        const parsed = Number(device.deviceId ?? device.id);
        if (Number.isFinite(parsed) && parsed > 0) {
          ids.add(parsed);
        }
      });
    });
    return Array.from(ids);
  }, [schedules]);
  const products = React.useMemo(() => {
    const productSet = new Set<string>();
    schedules.forEach((schedule) => {
      (autoPatrolCameraProducts[schedule.id] || []).forEach((product) => {
        const displayName = product?.metric_label?.display_name;
        if (displayName && !isAutopatrolUnavailableProduct(displayName)) {
          productSet.add(displayName);
        }
      });
    });
    const discoveredProducts = Array.from(productSet);
    const discoveredProductSet = new Set(discoveredProducts);

    const orderedKnownProducts = ORDERED_EDIT_MODE_PRODUCTS.filter((productName) =>
      discoveredProductSet.has(productName)
    );
    const unknownProducts = discoveredProducts
      .filter((productName) => !ORDERED_EDIT_MODE_PRODUCTS.includes(productName))
      .sort((a, b) => a.localeCompare(b));

    return [...orderedKnownProducts, ...unknownProducts];
  }, [schedules, autoPatrolCameraProducts]);

  const [draftSelections, setDraftSelections] = React.useState<
    Record<number, Record<string, boolean>>
  >({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [bulkResultSnackbar, setBulkResultSnackbar] = React.useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });

  React.useEffect(() => {
    if (!open) return;
    setDraftSelections(createInitialDraft(schedules, autoPatrolCameraProducts));
  }, [open, schedules, autoPatrolCameraProducts]);

  const isColumnChecked = (productName: string) =>
    schedules.length > 0 &&
    schedules.every((schedule) => draftSelections[schedule.id]?.[productName] === true);

  const isColumnIndeterminate = (productName: string) => {
    const selectedCount = schedules.filter(
      (schedule) => draftSelections[schedule.id]?.[productName] === true
    ).length;
    return selectedCount > 0 && selectedCount < schedules.length;
  };

  const handleToggleCell = (scheduleId: number, productName: string) => {
    setDraftSelections((previous) => ({
      ...previous,
      [scheduleId]: {
        ...(previous[scheduleId] || {}),
        [productName]: previous[scheduleId]?.[productName] !== true,
      },
    }));
  };

  const handleToggleColumn = (productName: string) => {
    const enableColumn = !isColumnChecked(productName);
    setDraftSelections((previous) => {
      const next = { ...previous };
      schedules.forEach((schedule) => {
        next[schedule.id] = {
          ...(next[schedule.id] || {}),
          [productName]: enableColumn,
        };
      });
      return next;
    });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const updates: Array<{
      scheduleId: number;
      productId: number;
      enabled: boolean;
    }> = [];
    const nextProductsBySchedule = { ...autoPatrolCameraProducts };

    schedules.forEach((schedule) => {
      const scheduleProducts = autoPatrolCameraProducts[schedule.id] || [];
      const updatedScheduleProducts = scheduleProducts.map((product) => {
        const displayName = product.metric_label?.display_name;
        if (!displayName || isAutopatrolUnavailableProduct(displayName)) return product;
        const nextEnabled = Boolean(draftSelections[schedule.id]?.[displayName]);
        if (nextEnabled !== Boolean(product.enabled)) {
          updates.push({
            scheduleId: schedule.id,
            productId: product.id,
            enabled: nextEnabled,
          });
        }
        return {
          ...product,
          enabled: nextEnabled,
        };
      });
      nextProductsBySchedule[schedule.id] = updatedScheduleProducts;
    });
    const siteCamerasFromStorage = JSON.parse(
      localStorage.getItem("siteCameras") || "{}"
    ) as { results?: Array<{ id?: number | string }> };
    const siteCameraRowIds = Array.isArray(siteCamerasFromStorage?.results)
      ? siteCamerasFromStorage.results
          .map((camera) => Number(camera?.id))
          .filter((id: number) => Number.isFinite(id))
      : [];
    const affectedInSiteRows = affectedCameraIds.filter((id) =>
      siteCameraRowIds.includes(id)
    );
    const refreshCameraIds =
      affectedInSiteRows.length > 0
        ? affectedInSiteRows
        : siteCameraRowIds.length > 0
        ? siteCameraRowIds
        : affectedCameraIds;

    if (updates.length === 0) {
      setIsSubmitting(false);
      onClose();
      return;
    }

    const submitUpdatesRecursively = async (index: number): Promise<void> => {
      if (index >= updates.length) return;
      const update = updates[index];
      await updateScheduleProducts(
        update.productId,
        update.enabled,
        update.scheduleId,
        navigate
      );
      await submitUpdatesRecursively(index + 1);
    };

    try {
      await submitUpdatesRecursively(0);
      setAutoPatrolCameraProducts(nextProductsBySchedule);
      if (customerId > 0 && refreshCameraIds.length > 0) {
        try {
          await prefetchBulkDialogActiveCameraProducts(
            navigate,
            customerId,
            refreshCameraIds
          );
        } catch {
          // Preserve successful submit UX even if camera-row refresh endpoint fails.
        }
      }
      window.dispatchEvent(new CustomEvent("refreshSiteCamerasTable"));
      setBulkResultSnackbar({
        open: true,
        message: `Bulk product update completed for ${updates.length} product update${
          updates.length === 1 ? "" : "s"
        }.`,
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <BackdropLoadingScreen
        openState={isSubmitting}
        stackAboveModal
        message="Applying bulk update..."
      />
      <Dialog
        open={open}
        onClose={() => {
          if (!isSubmitting) onClose();
        }}
        disableEscapeKeyDown={isSubmitting}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ fontFamily: "Mulish", fontWeight: 700 }}>
          Product Bulk Update
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontFamily: "Mulish",
                      fontWeight: 700,
                      minWidth: 200,
                      width: 200,
                    }}
                  >
                  </TableCell>
                  {products.map((productName) => {
                    const checkboxKey = toCheckboxKey(productName);
                    const productColor =
                      EDIT_MODE_COLOR_BY_PRODUCT[productName] ?? "#616161";
                    return (
                      <TableCell
                        key={productName}
                        align="center"
                        sx={{
                          fontFamily: "Mulish",
                          fontWeight: 700,
                          verticalAlign: "bottom",
                          pb: "5px",
                          px: 2.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <div className="flex min-h-[76px] flex-col items-center justify-end">
                          <span style={{ color: "#000000", whiteSpace: "nowrap" }}>
                            {getProductDisplayLabel(productName)}
                          </span>
                          <Checkbox
                            checked={isColumnChecked(productName)}
                            indeterminate={isColumnIndeterminate(productName)}
                            onChange={() => handleToggleColumn(productName)}
                            data-testid={`product-bulk-column-${checkboxKey}`}
                            sx={{
                              px: 1.5,
                              color: productColor,
                              "&.Mui-checked": {
                                color: productColor,
                              },
                              "&.MuiCheckbox-indeterminate": {
                                color: productColor,
                              },
                            }}
                            inputProps={{
                              "aria-label": `Toggle ${productName} for all schedules`,
                            }}
                          />
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((schedule) => (
                  <TableRow key={schedule.id}>
                    <TableCell
                      sx={{
                        fontFamily: "Mulish",
                        fontWeight: 600,
                        minWidth: 200,
                        width: 320,
                      }}
                    >
                      {schedule.title?.trim() || `Schedule ${schedule.id}`}
                    </TableCell>
                    {products.map((productName) => {
                      const checkboxKey = toCheckboxKey(productName);
                      const productColor =
                        EDIT_MODE_COLOR_BY_PRODUCT[productName] ?? "#616161";
                      return (
                        <TableCell
                          key={`${schedule.id}-${productName}`}
                          align="center"
                          sx={{ px: 2.5 }}
                        >
                          <Checkbox
                            checked={Boolean(draftSelections[schedule.id]?.[productName])}
                            onChange={() => handleToggleCell(schedule.id, productName)}
                            data-testid={`product-bulk-cell-${schedule.id}-${checkboxKey}`}
                            sx={{
                              px: 1.5,
                              color: productColor,
                              "&.Mui-checked": {
                                color: productColor,
                              },
                            }}
                            inputProps={{
                              "aria-label": `Toggle ${productName} for ${
                                schedule.title || `Schedule ${schedule.id}`
                              }`,
                            }}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <OrangeTextButton onClick={onClose} text="CANCEL" disabled={isSubmitting} />
          <OrangeTextButton
            onClick={() => void handleSubmit()}
            text={isSubmitting ? "SUBMITTING..." : "SUBMIT"}
            disabled={isSubmitting}
          />
        </DialogActions>
      </Dialog>
      <Portal>
        <Snackbar
          open={bulkResultSnackbar.open}
          autoHideDuration={6000}
          onClose={() => setBulkResultSnackbar({ open: false, message: "" })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity="success"
            onClose={() => setBulkResultSnackbar({ open: false, message: "" })}
            sx={{ width: "100%", fontFamily: "Mulish" }}
          >
            {bulkResultSnackbar.message}
          </MuiAlert>
        </Snackbar>
      </Portal>
    </>
  );
};
